import { Injectable, Injector, Inject } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LoginService } from '../services/login.service';

/**
 * HTTP Error Handling Interceptor
 *
 * Handles HTTP errors globally across the application:
 * 1. 401 Unauthorized - Logs user out and redirects to login page
 * 2. Network errors (status 0) - Detects connection failures like ERR_CONNECTION_REFUSED
 *
 * This matches and extends the AngularJS httpInterceptor behavior.
 *
 * Note: Uses lazy injection via Injector to avoid circular dependency:
 * ErrorHandlingInterceptor → LoginService → HttpClient → ErrorHandlingInterceptor
 */
@Injectable()
export class ErrorHandlingInterceptor implements HttpInterceptor {
  constructor(@Inject(Injector) private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle 401 Unauthorized - matches AngularJS interceptor behavior
        // Handle 401 Unauthorized - matches AngularJS interceptor behavior
        if (error.status === 401) {
          console.warn('401 Unauthorized - logging out user');

          // Lazy inject services to avoid circular dependency
          const loginService = this.injector.get(LoginService);
          const router = this.injector.get(Router);

          loginService.IsLoggedOut();
          router.navigate(['/login']);
          return throwError(() => error);
        }

        // Handle network errors (status 0)
        if (error.status === 0) {
          console.error('Network error detected:', error);

          // Network errors have error instanceof ProgressEvent
          if (error.error instanceof ProgressEvent) {
            console.error(
              'Cannot connect to server. This could be:',
              '\n- ERR_CONNECTION_REFUSED (server not running)',
              '\n- Network timeout',
              '\n- CORS error',
              '\n- DNS resolution failure'
            );

            // You can add user notification here in the future
            // For example: this.notificationService.showError('Cannot connect to server');
          }

          return throwError(() => error);
        }

        // Handle 504 Gateway Timeout (webpack proxy cannot reach backend)
        if (error.status === 504) {
          console.error('504 Gateway Timeout - Backend server is not responding');
          console.error('This usually means the backend server is not running.');

          // You can add user notification here in the future
          // For example: this.notificationService.showError('Backend-servern svarar inte');

          return throwError(() => error);
        }

        // For all other errors, just pass them through
        return throwError(() => error);
      })
    );
  }
}
